import { CaretDownOutlined } from '@ant-design/icons'
import { Checkbox, Flex } from 'antd'
import { hasPinnedActionCol } from 'assets/styles/DatatableStyles/styles'
import AppResource from 'general/constants/AppResource'
import Utils from 'general/utils/Utils'
import { useEffect, useMemo, useState } from 'react'
import DataTable from 'react-data-table-component'
import { useTranslation } from 'react-i18next'
import Empty from '../Empty'
import Loading from '../Loading'
import Pagination from '../Pagination'
import ResizableHeader from './components/ResizableHeader'

/**
 * @template T
 * @param {Object} prop
 * @param {string} [prop.tableKey] - Unique key for storing column widths
 * @param {import('react').HTMLAttributes['className']} [prop.wrapperClassName] - Column of the table
 * @param {import('react-data-table-component').TableColumn<T>[]} prop.columns - Column of the table
 * @param {Array} prop.dataSource - Data of the table
 * @param {boolean} [prop.isLoading] - Loading state of the table
 * @param {boolean} [prop.isClickable] - Whether the row is clickable
 * @param {boolean} [prop.isSelectable] - Whether the row has checkbox
 * @param {{total: number,
 *  rowsPerPage: number,
 *  currentPage: number,
 *  onChangePage: (page: number) => void,
 *  onChangeRowsPerPage: (rowsPerPage: number) => void
 * }} prop.pagination - Pagination setting for the table
 * @param {{
 *  selectableRowSelected: (row: any) => boolean,
 *  clearSelectedRows: boolean,
 *  handleSelectedRows:  (selected: {
 * allSelected: boolean;
 * selectedCount: number;
 * selectedRows: any[];
 * }) => void
 * }} [prop.selectedRows] - An object contains selected rows properties when isSelectable is true
 * @param {import('react-data-table-component').TableProps<T>['onRowClicked']} [prop.handleClickRow] - Handle click row event
 * @param {import('react-data-table-component').TableProps<T>['onRowDoubleClicked']} [prop.handleDoubleClickRow] - Handle double click row event
 * @param {string} prop.scrollY - Fixed header height
 * @param {import('react-data-table-component').TableProps<T>['customStyles']} [prop.customTableStyle] - Custom style for the table
 * @param {boolean} [prop.noShowEmpty] - Whether to show empty component, default is false
 * @param {boolean} [prop.persistTableHead] - Whether to persist table head, default is false
 * @param {import('react-data-table-component').ConditionalStyles<T>[]} [prop.conditionalRowStyles] - Conditional row styles
 * @param {boolean} [prop.showStripe] - Stripe style
 * @param {boolean} [prop.resizableColumns] - Whether to allow resizing table columns
 * @param {boolean} [prop.sortServer] - Whether sorting is handled by the server
 * @param {import('react-data-table-component').TableProps<T>['onSort']} [prop.onSort]
 * @param {string|number} [prop.defaultSortFieldId]
 * @param {boolean} [prop.defaultSortAsc]
 */
function CustomDataTable(prop) {
  const {
    tableKey,
    wrapperClassName,
    columns,
    dataSource,
    isLoading,
    pagination,
    isClickable,
    isSelectable,
    selectedRows,
    handleClickRow,
    handleDoubleClickRow,
    scrollY,
    customTableStyle,
    noShowEmpty = false,
    persistTableHead = false,
    conditionalRowStyles,
    showStripe = true,
    resizableColumns = false,
    sortServer = false,
    onSort,
    defaultSortFieldId,
    defaultSortAsc,
  } = prop
  const { t } = useTranslation()
  const [selectedState] = useState(selectedRows)
  const clearSelectedRows = useMemo(
    () => selectedRows?.clearSelectedRows,
    [selectedRows?.clearSelectedRows],
  )

  const getMinWidth = (column) => {
    const minWidth = parseInt(column.minWidth, 10)

    return Number.isNaN(minWidth) ? 80 : minWidth
  }

  const getDefaultWidth = (column) => {
    const width = parseInt(column.width, 10)
    const minWidth = getMinWidth(column)

    if (Number.isNaN(width)) {
      return Math.max(150, minWidth)
    }

    return Math.max(width, minWidth)
  }

  const [columnWidths, setColumnWidths] = useState({})

  useEffect(() => {
    if (!tableKey || !resizableColumns) {
      setColumnWidths({})
      return
    }

    const storageKey = `${tableKey}-column-widths`
    const savedWidths = localStorage.getItem(storageKey)

    let storedWidths = {}

    try {
      storedWidths = savedWidths ? JSON.parse(savedWidths) : {}
    } catch {
      storedWidths = {}
    }

    const newWidths = { ...storedWidths }

    columns.forEach((column) => {
      if (!column.id) return

      if (newWidths[column.id] == null) {
        newWidths[column.id] = getDefaultWidth(column)
      }
    })

    localStorage.setItem(storageKey, JSON.stringify(newWidths))

    setColumnWidths(newWidths)
  }, [tableKey, resizableColumns, columns])

  const handleResize = (columnId, width) => {
    const column = columns.find((item) => item.id === columnId)

    if (!column) return

    const minWidth = getMinWidth(column)
    const newWidth = Math.max(width, minWidth)

    setColumnWidths((prev) => {
      const newWidths = {
        ...prev,
        [columnId]: newWidth,
      }

      if (tableKey) {
        localStorage.setItem(
          `${tableKey}-column-widths`,
          JSON.stringify(newWidths),
        )
      }

      return newWidths
    })
  }

  const tableColumns = useMemo(() => {
    if (!resizableColumns) return columns

    return columns.map((column) => {
      if (!column.id) return column

      const minWidth = getMinWidth(column)
      const width = Math.max(
        columnWidths[column.id] ?? getDefaultWidth(column),
        minWidth,
      )

      return {
        ...column,
        width: `${width}px`,
        minWidth: `${minWidth}px`,
        name: (
          <ResizableHeader
            column={column}
            columnId={column.id}
            currentWidth={width}
            minWidth={minWidth}
            onResize={handleResize}
          />
        ),
      }
    })
  }, [columns, columnWidths, resizableColumns])

  const customStyles = useMemo(() => {
    const baseStyles = customTableStyle ?? hasPinnedActionCol

    if (!resizableColumns) {
      return baseStyles
    }

    return {
      ...baseStyles,
      headCells: {
        ...baseStyles?.headCells,
        style: {
          ...baseStyles?.headCells?.style,
          padding: 0,
          overflow: 'visible',
        },
      },
    }
  }, [customTableStyle, resizableColumns])

  return (
    <>
      <DataTable
        className={
          scrollY ? '' : Utils.cn('max-h-100 flex-grow-1', wrapperClassName)
        }
        columns={tableColumns}
        data={dataSource}
        fixedHeader
        fixedHeaderScrollHeight={scrollY ?? 'calc(100vh - 300px)'}
        striped={showStripe}
        responsive
        persistTableHead={persistTableHead}
        sortServer={sortServer}
        onSort={onSort}
        defaultSortFieldId={defaultSortFieldId}
        defaultSortAsc={defaultSortAsc}
        customStyles={customStyles}
        noDataComponent={
          noShowEmpty ? null : (
            <div className="pt-12">
              <Empty
                text={t('NoData')}
                visible={false}
                imageEmpty={AppResource.icons.icEmptyBox}
                imageEmptyPercentWidth={170}
              />
            </div>
          )
        }
        progressPending={isLoading}
        progressComponent={
          <Loading showBackground={false} message={`${t('Loading')}...`} />
        }
        sortIcon={
          <CaretDownOutlined />
          // <i className="fa-light fa-arrow-down mx-2" />
        }
        {...(isSelectable && {
          selectableRows: true,
          selectableRowsVisibleOnly: false,
          selectableRowsHighlight: true,
          selectableRowsComponent: Checkbox,
          selectableRowsComponentProps: {
            indeterminate: (state) => state,
          },
          selectableRowSelected: selectedState.selectableRowSelected,
          clearSelectedRows,
          onSelectedRowsChange: selectedState.handleSelectedRows,
        })}
        {...(isClickable && {
          onRowClicked: handleClickRow ? handleClickRow : () => {},
          onRowDoubleClicked: handleDoubleClickRow
            ? handleDoubleClickRow
            : () => {},
          pointerOnHover: true,
        })}
        highlightOnHover
        conditionalRowStyles={conditionalRowStyles}
      />
      {pagination && pagination?.total > 0 && (
        <Flex align="center" justify="center" className="mt-5">
          <Pagination
            rowsPerPage={pagination.rowsPerPage}
            rowCount={pagination.total}
            perPageItemsList={pagination.perPageItemsList}
            currentPage={pagination.currentPage}
            onChangePage={pagination.onChangePage}
            onChangeRowsPerPage={pagination.onChangeRowsPerPage}
          />
        </Flex>
      )}
    </>
  )
}

export default CustomDataTable
